import React, { useState, useEffect, useRef } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  LogOut, 
  Image as ImageIcon, 
  Plus, 
  Trash2, 
  GripVertical,
  Loader2,
  Check,
  X,
  Upload
} from 'lucide-react';

const CAROUSEL_PAGES = [
  { id: 'chaussures', name: 'Chaussures' },
  { id: 'textile', name: 'Textile' },
  { id: 'accessoires', name: 'Accessoires' },
  { id: 'nutrition', name: 'Nutrition' },
];

const AdminDashboard = () => {
  const { admin, logout, token } = useAdminAuth();
  const [activePage, setActivePage] = useState('chaussures');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch carousel images
  const fetchCarousel = async (page) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/carousels/${page}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setImages(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching carousel:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarousel(activePage);
  }, [activePage]);

  // Upload file
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/carousels/${activePage}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setImages(data.carousel?.images || []);
        showMessage('success', 'Image uploadée avec succès');
      } else {
        const error = await response.json();
        showMessage('error', error.detail || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      showMessage('error', 'Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Add new image via URL
  const handleAddImage = async () => {
    if (!newImageUrl.trim()) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/carousels/${activePage}/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: newImageUrl, alt: '' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setImages(data.images || []);
        setNewImageUrl('');
        showMessage('success', 'Image ajoutée avec succès');
      }
    } catch (error) {
      showMessage('error', 'Erreur lors de l\'ajout de l\'image');
    } finally {
      setSaving(false);
    }
  };

  // Delete image
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/carousels/${activePage}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setImages(data.images || []);
        showMessage('success', 'Image supprimée');
      }
    } catch (error) {
      showMessage('error', 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  // Reorder images
  const handleMoveImage = async (index, direction) => {
    const newImages = [...images];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= newImages.length) return;
    
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    
    // Update order values
    newImages.forEach((img, i) => {
      img.order = i;
    });
    
    setImages(newImages);
    
    // Save to backend
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/admin/carousels/${activePage}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ images: newImages })
      });
      showMessage('success', 'Ordre mis à jour');
    } catch (error) {
      showMessage('error', 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-neuropolitical font-bold text-gray-900">Administration</h1>
            <p className="text-sm text-gray-600">{admin?.email}</p>
          </div>
          <Button variant="outline" onClick={logout} className="gap-2">
            <LogOut size={18} />
            Déconnexion
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.type === 'success' ? <Check size={18} /> : <X size={18} />}
            {message.text}
          </div>
        )}

        {/* Page Tabs */}
        <div className="bg-white rounded-xl shadow-sm p-2 mb-6">
          <div className="flex gap-2 flex-wrap">
            {CAROUSEL_PAGES.map((page) => (
              <button
                key={page.id}
                onClick={() => setActivePage(page.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activePage === page.id
                    ? 'bg-emerald-700 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {page.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <ImageIcon className="text-emerald-700" size={24} />
            <h2 className="text-lg font-semibold">Carrousel {CAROUSEL_PAGES.find(p => p.id === activePage)?.name}</h2>
          </div>

          {/* Upload Section */}
          <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 mb-1">Uploader une image</h3>
                <p className="text-sm text-gray-500">Formats acceptés : JPG, PNG, WebP, GIF</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-emerald-700 hover:bg-emerald-800 gap-2"
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {uploading ? 'Upload en cours...' : 'Choisir un fichier'}
              </Button>
            </div>
          </div>

          {/* Add Image via URL */}
          <div className="mb-8">
            <p className="text-sm text-gray-500 mb-2">Ou ajouter via URL :</p>
            <div className="flex gap-3">
              <Input
                placeholder="URL de l'image (https://...)"
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleAddImage} 
                disabled={saving || !newImageUrl.trim()}
                variant="outline"
                className="gap-2"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                Ajouter URL
              </Button>
            </div>
          </div>

          {/* Images List */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <Loader2 size={32} className="animate-spin mx-auto mb-2" />
              Chargement...
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ImageIcon size={48} className="mx-auto mb-3 opacity-50" />
              <p>Aucune image dans ce carrousel</p>
              <p className="text-sm">Ajoutez des images en utilisant le formulaire ci-dessus</p>
            </div>
          ) : (
            <div className="space-y-3">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg group"
                >
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleMoveImage(index, -1)}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <GripVertical size={16} className="text-gray-400 mx-auto" />
                    <button
                      onClick={() => handleMoveImage(index, 1)}
                      disabled={index === images.length - 1}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    <img
                      src={image.url}
                      alt={image.alt || 'Image carrousel'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/96?text=Erreur';
                      }}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600 truncate">{image.url}</p>
                    <p className="text-xs text-gray-400">Position: {index + 1}</p>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteImage(image.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
